import decode from "jwt-decode";

export function getExpiration(token: string): Date {
  const { exp } = decode<{ exp: number }>(token);
  return new Date(exp * 1000);
}

/**
 * Whether a token's `exp` has passed — the single expiry check for the app.
 *
 * Fails closed, and does so explicitly rather than by relying on a thrown error.
 * `getExpiration` only throws when the string is not a decodable JWT; a
 * well-formed token that simply omits `exp` decodes cleanly and yields
 * `Date(NaN)`. Because `NaN <= Date.now()` is false under IEEE-754, a naive
 * comparison reports such a token as *live*, which would keep an authenticated
 * subtree and its background pollers mounted indefinitely. Anything whose expiry
 * cannot be read as a finite instant is therefore treated as already expired.
 */
export function isExpired(token: string, now: number = Date.now()): boolean {
  if (!token) {
    return true;
  }

  let expiresAt: number;
  try {
    expiresAt = getExpiration(token).getTime();
  } catch {
    // Not a decodable JWT.
    return true;
  }

  if (!Number.isFinite(expiresAt)) {
    return true;
  }

  return expiresAt <= now;
}

export function getName(token: string): string | undefined {
  const { name } = decode<{ name: string | undefined }>(token);
  return name;
}

export function getEmail(token: string): string | undefined {
  if (!token || token.length === 0) {
    return undefined;
  }

  const jwt = decode<any>(token);
  return jwt.email ?? jwt.upn;
}

export function getEmailFromStorage(stored: string): string | undefined {
  if (!stored || stored.length === 0) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(stored);
    if (parsed.id_token) {
      return getEmail(parsed.id_token);
    }
  } catch {
    // not JSON — treat as raw JWT
  }

  return getEmail(stored);
}
